import { useCallback, useState } from "react";
import useGetWorkOrder from "./useGetWorkOrder";
import { getLinkedDocInfoList } from "../api/WorkOrders";
import { useWorkOrderStore } from "../store/wo-order";
import useLoadCompWizardSections from "./useLoadCompWizardSections";
import useGetValidStatusValues from "./work-order/useGetValidStatusValues";
import useGetMetaData from "./work-order/useGetMetaData";
import useGetCustomValues from "./work-order/useGetCustomValues";
import useGetCustomFields from "./work-order/useGetCustomFields";
import useGetLongDescriptionMetaData from "./work-order/useGetLongDescriptionMetaData";
import { getCurrency } from "../util/PlantHelper";
import useGetResourcesFields from "./work-order/useGetResourcesFields";
import useGetPermits from "./work-order/useGetPermits";
import useGetComments from "./work-order/useGetComments";
import useGetLaborLogFields from "./work-order/useGetLaborLogFields";
import useGetLaborLogs from "./work-order/useGetLaborLogs";
import useGetTimeEntryAccess from "./work-order/useGetTimeEntryAccess";
import useGetMeterMetadata from "./work-order/useGetMeterMetadata";
import useGetMeterReading from "./work-order/useGetMeterReading";
import useGetAnalysisCodes from "./work-order/useGetAnalysisCodes";
// import useGetMeterReadingHistory from "./work-order/useGetMeterReadingHistory";
import useGetNoOfMeterHistoryDisplay from "./work-order/useGetNoOfMeterHistoryDisplay";
import { WorkOrder } from "../models/workorders/WorkOrder";
import useGetInspection from "./work-order/inspection/useGetInspection";
import useGetCreateWoMetaData from "./work-order/useGetCreateWoMetaData";
import useGetCustomColumns from "./work-order/useGetCustomColumns";
import useGetCascadeValues from "./validation/useGetCascadeValues";
import { getUserId } from "../util/UserHelper";
import { useUserStore } from "../store/user";
import useGetTimeEntryMetaData from "./work-order/time-entry/useGetTimeEntryMetaData";
import useGetCraftsForEmployee from "./work-order/time-entry/useGetCraftsForEmployee";
import useGetRateList from "./work-order/time-entry/useGetRateList";
import useGetWoCompletionFields from "./work-order/useGetUserValidValues";
import useGetDefaultWOCompletionStatus from "./work-order/useGetDefaultWOCompletionStatus";
import useGetPlannedListItems from "./work-order/issue-return/useGetPlannedListItems";
import { useSettingsStore } from "../store/settings";
import { Employee } from "../models/employees/Employees";
import { getInventoryControl } from "../api/PlantSetups";
import useGetReturnDetailsColumns from "./work-order/issue-return/useGetReturnDetailsColumns";
import useGetIssueDetailsColumns from "./work-order/issue-return/useGetIssueDetailsColumns";
import { getDefaultFilterForIssueScreen } from "../api/UserSetups";
import useGetEmployeesLiteList from "./user/useGetEmployeesLiteList";
import useGetTestEquipmentListAndRule from "./work-order/inspection/useGetTestEquipmentListAndRule";
import useGetIsCAARequiredForCompletion from "./work-order/completion/useGetIsCAARequiredForCompletion";
import useGetWorkOrderControl from "./work-order/useGetWorkOrderControl";
import useGetLinkedInspection from "./work-order/useGetLinkedInspection";

/**
 * Download the work orders and cache it for offline access
 */
export default function useDownloadWorkOrdersForOfflineAccess() {
  const [isDownloading, setIsDownloading] = useState(false);

  const { trigger: getWorkOrder } = useGetWorkOrder();
  const { loadCompWizardSections } = useLoadCompWizardSections();
  const { getValidStatusValues } = useGetValidStatusValues();
  const { getMetaData } = useGetMetaData();
  const { getCustomValues } = useGetCustomValues();
  const { getCustomFields } = useGetCustomFields();
  const { getLongDescriptionMetaData } = useGetLongDescriptionMetaData();
  const { getResourcesFields } = useGetResourcesFields();
  const { getPermits } = useGetPermits();
  const { getComments } = useGetComments();
  const { getTimeEntryAccessForWo } = useGetTimeEntryAccess();
  const { getLaborLogFields } = useGetLaborLogFields();
  const { getLaborLogs } = useGetLaborLogs();
  const { getMeterMetadata } = useGetMeterMetadata();
  const { getMeterReading } = useGetMeterReading();
  // const { getMeterReadingHistory } = useGetMeterReadingHistory();
  const { getNoOfMeterHistoryDisplay } = useGetNoOfMeterHistoryDisplay();
  const { getAnalysisCodes } = useGetAnalysisCodes();
  const { getInspection } = useGetInspection();
  const { getCreateWoMetaData } = useGetCreateWoMetaData();
  const { getCustomColumns } = useGetCustomColumns();
  const { getCascadedValues } = useGetCascadeValues();
  const { getTimeEntryMetaData } = useGetTimeEntryMetaData();
  const { getCraftsForEmployee } = useGetCraftsForEmployee();
  const { getRateList } = useGetRateList();
  const { getWoCompletionFields } = useGetWoCompletionFields();
  const { getDefaultWOCompletionStatus } = useGetDefaultWOCompletionStatus();
  const { getPlannedListItems } = useGetPlannedListItems();
  const { getReturnDetailsColumns } = useGetReturnDetailsColumns();
  const { getIssueDetailsColumns } = useGetIssueDetailsColumns();
  const { getLiteListOfEmployees } = useGetEmployeesLiteList();
  const { getTestEquipmentListAndRule } = useGetTestEquipmentListAndRule();
  const { getIsCAARequiredForCompletion } = useGetIsCAARequiredForCompletion();
  const { getWorkOrderControlData } = useGetWorkOrderControl();
  const { getLinkedInspection } = useGetLinkedInspection();

  const setWoDocuments = useWorkOrderStore((state) => state.setWoDocuments);
  const setCurrency = useWorkOrderStore((state) => state.setWoPlantCurrency);
  const setNegativeQtyOnHandMessage = useWorkOrderStore(
    (state) => state.setNegativeQtyOnHandMessage
  );
  const setDefaultFilterForIssueScreen = useWorkOrderStore(
    (state) => state.setDefaultFilterForIssueScreen
  );

  const setLoggedInUserId = useUserStore((state) => state.setLoggedInEmployee);

  const getWoDocuments = useCallback(
    async (wo: string) => {
      const response = await getLinkedDocInfoList(wo);
      if (!response.isError) {
        setWoDocuments(wo, response.data);
      }
    },
    [setWoDocuments]
  );

  // const getMeterReadingHistoryData = useCallback(
  //   async (woNumbers: string) => {
  //     const meterReadings = useWorkOrderStore
  //       .getState()
  //       .getMeterReadingsForWo(woNumbers);
  //     if (!meterReadings?.length) return; //got no meter reading
  //     for (const meter of meterReadings) {
  //       if (meter.Meter) await getMeterReadingHistory(woNumbers, meter.Meter);
  //     }
  //   },
  //   [getMeterReadingHistory]
  // );

  const getLoggedInEmployee = useCallback(async () => {
    const response = await getLiteListOfEmployees();
    const employees = response.data as Employee[];
    const userId = await getUserId();
    const employee = employees.find((employee) => {
      return employee.EmployeeId === userId;
    });
    setLoggedInUserId(employee);
    return userId;
  }, [getLiteListOfEmployees, setLoggedInUserId]);

  const getNegativeQtyOnHandMessage = useCallback(async () => {
    const response = await getInventoryControl();
    setNegativeQtyOnHandMessage(
      (response.data?.NegativeQtyOnHandMessage ?? "") as string
    );
  }, [setNegativeQtyOnHandMessage]);

  const getDefaultFilterForIssueWOScreen = useCallback(async () => {
    const filter = await getDefaultFilterForIssueScreen();
    setDefaultFilterForIssueScreen(filter);
  }, [setDefaultFilterForIssueScreen]);

  const downloadAllInspections = useCallback(
    async (woNumber: string) => {
      const linkedInspections =
        useWorkOrderStore.getState().getLinkedInspectionsForWo(woNumber) ?? [];
      for (const ins of linkedInspections) {
        await getInspection(woNumber, ins.InspectionId.toString());
      }
    },
    [getInspection]
  );

  const trigger = useCallback(
    async (woNumbers: string[]) => {
      if (!woNumbers.length) return;
      const defaultStore = useSettingsStore.getState().defaultStore;
      setIsDownloading(true);
      for (const woNumber of woNumbers) {
        const res = await getWorkOrder(woNumber);
        const workOrder = res.data as WorkOrder;
        await getIsCAARequiredForCompletion({
          woEquipment: workOrder.Equipment ?? "",
          woEventDateCreated: workOrder.Events.DateCreated,
          woEventTimeCreated: workOrder.Events.TimeCreated,
          woNumber,
        });
        await getWorkOrderControlData(workOrder);
        await getLinkedInspection(woNumber);
        await getWoDocuments(woNumber);
        await loadCompWizardSections(woNumber);
        await getValidStatusValues(woNumber);
        await getMetaData(woNumber);
        await getCustomValues(woNumber);
        await getCustomFields(woNumber);
        await getLongDescriptionMetaData(woNumber);
        await getResourcesFields(woNumber);
        const curr = await getCurrency();
        setCurrency(woNumber, curr);
        await getPermits(woNumber);
        await getComments(woNumber);
        await getTimeEntryAccessForWo(woNumber);
        await getLaborLogFields(woNumber);
        await getLaborLogs(woNumber);
        await getAnalysisCodes(woNumber);
        //Commenting meter reading
        await getMeterReading(woNumber);
        // await getMeterReadingHistoryData(woNumber);

        //get associated inspections [762416]:Don't download meter readings
        await downloadAllInspections(woNumber);
        if (defaultStore) {
          //get associated issue-material for default store
          await getPlannedListItems({
            isIssueMaterial: true,
            storesLocation: defaultStore,
            workOrder: woNumber,
            requisition: "",
          });
          //get associated return-material for default store
          await getPlannedListItems({
            isIssueMaterial: false,
            storesLocation: defaultStore,
            workOrder: woNumber,
            requisition: "",
          });
        }
      }
      //download only once and use it for all the work orders
      await getMeterMetadata();
      await getMeterMetadata(true);
      await getMeterMetadata(false, true);
      await getNoOfMeterHistoryDisplay();
      await getCreateWoMetaData();
      await getCustomColumns();
      await getCascadedValues();
      const userId = await getLoggedInEmployee();
      await getTimeEntryMetaData();
      await getCraftsForEmployee(userId ?? "");
      await getRateList();
      await getWoCompletionFields();
      await getDefaultWOCompletionStatus();
      await getNegativeQtyOnHandMessage();
      await getReturnDetailsColumns();
      await getIssueDetailsColumns();
      await getDefaultFilterForIssueWOScreen();
      await getTestEquipmentListAndRule();
      setIsDownloading(false);
    },
    [
      downloadAllInspections,
      getAnalysisCodes,
      getCascadedValues,
      getComments,
      getCraftsForEmployee,
      getCreateWoMetaData,
      getCustomColumns,
      getCustomFields,
      getCustomValues,
      getDefaultFilterForIssueWOScreen,
      getDefaultWOCompletionStatus,
      getIsCAARequiredForCompletion,
      getIssueDetailsColumns,
      getLaborLogFields,
      getLaborLogs,
      getLinkedInspection,
      getLoggedInEmployee,
      getLongDescriptionMetaData,
      getMetaData,
      getMeterMetadata,
      getMeterReading,
      getNegativeQtyOnHandMessage,
      getNoOfMeterHistoryDisplay,
      getPermits,
      getPlannedListItems,
      getRateList,
      getResourcesFields,
      getReturnDetailsColumns,
      getTestEquipmentListAndRule,
      getTimeEntryAccessForWo,
      getTimeEntryMetaData,
      getValidStatusValues,
      getWoCompletionFields,
      getWoDocuments,
      getWorkOrder,
      getWorkOrderControlData,
      loadCompWizardSections,
      setCurrency,
    ]
  );

  return { trigger, isDownloading };
}
